import { FC, Fragment } from 'react';

import styles from './ClientReviewCardNew.module.scss';
import classNames from 'classnames';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import StarRating from '@z-components/atoms/StarRating';
import ReadMore from '@z-components/atoms/ReadMore';

interface Props {
  age?: 'age10' | 'age20' | 'age30' | 'age40' | 'age50' | 'age60over';
  compact?: boolean;
  inline?: boolean;
  jobTypeName?: {
    en: string;
    ja: string;
  };
  language?: 'en' | 'ja';
  message?: string;
  nickname?: string;
  prefecture?: string;
  pricing?: number;
  questionAnswers?: Array<{ answer?: string; question?: string }>;
  rating?: number;
  ratingPersonality?: number;
  ratingQuality?: number;
  time?: number;
  title?: string;
}

const AGE_LABELS = {
  age10: '10代',
  age20: '20代',
  age30: '30代',
  age40: '40代',
  age50: '50代',
  age60over: '60代以上',
};

const OTHER_MESSAGE = 'その他のご意見・ご感想';

dayjs.extend(relativeTime);

// eslint-disable-next-line no-magic-numbers
const THIRTY_ONE_DAYS_IN_MS = 31 * 24 * 60 * 60 * 1000;

const ClientReviewCardNew: FC<Props> = ({
  age,
  compact = false,
  inline = false,
  jobTypeName,
  language = 'ja',
  message = '',
  nickname = 'ゲスト',
  prefecture = '',
  pricing,
  questionAnswers = [],
  rating,
  ratingPersonality,
  ratingQuality,
  time = 0,
  title = '',
}) => {
  const formattedTime =
    new Date().getTime() - time > THIRTY_ONE_DAYS_IN_MS
      ? dayjs(time).locale(language).format('YYYY/MM')
      : dayjs(time).locale(language).fromNow();

  const hasQuestionAnswers = questionAnswers.length > 0;

  const reviewContent = (
    <div className={styles['review-content']}>
      {hasQuestionAnswers && (
        <dl className={styles['question-answers']}>
          {questionAnswers.map(({ question, answer }) => (
            <Fragment key={question}>
              <h4>
                <dt>{question}</dt>
              </h4>
              <dd>{answer}</dd>
            </Fragment>
          ))}
          {message && (
            <Fragment key={OTHER_MESSAGE}>
              <dt>{OTHER_MESSAGE}</dt>
              <dd>{message}</dd>
            </Fragment>
          )}
        </dl>
      )}

      {!hasQuestionAnswers && message && (
        <dl className={styles['question-answers']}>
          <dt>{title}</dt>
          <dd>{message}</dd>
        </dl>
      )}
    </div>
  );
  return (
    <article
      className={classNames(styles['client-review-card'], {
        [styles['client-review-card-border']]: !inline,
      })}
    >
      <div className={styles['rating-row']}>
        <StarRating displayNumber={true} height={20} rating={rating} />
        <p className={styles.time}>{formattedTime}</p>
      </div>

      <div className={styles['details-row']}>
        {jobTypeName && <div className={styles['job-type-name']}>{jobTypeName[language]}</div>}
        <ul className={styles['details-list']}>
          <li>{nickname}さん</li>
          {age && AGE_LABELS[age] && <li>{AGE_LABELS[age]}</li>}
          {prefecture && <li>{prefecture}</li>}
        </ul>

        <ul className={styles['details-list']}>
          {Number.isInteger(ratingQuality) && <li>サービス品質 {ratingQuality}</li>}
          {Number.isInteger(ratingPersonality) && <li>プロの人柄 {ratingPersonality}</li>}
          {Number.isInteger(pricing) && <li>金額 {(pricing ?? 0).toLocaleString(language)}</li>}
        </ul>
      </div>

      {compact && (hasQuestionAnswers || message) ? (
        <ReadMore language={language}>{reviewContent}</ReadMore>
      ) : (
        reviewContent
      )}
    </article>
  );
};

export default ClientReviewCardNew;
export type { Props as ClientReviewCardNewProps };
